import urlService from 'common/services/api/urlService';
import { Context } from 'common/store/common/constants';
import { AssessmentState } from '../store/assessment/reducer';
import store from '../store';
import { RootState } from '../store/rootReducer';
import { hasCurrentUserEvaluatorPrivilege, hasCurrentUserPlannerPrivilege } from './permissions/PermissionService';
import { OptionsMenuActionsName } from '../store/optionsMenu/types';
import submissionService from '../features/submission/services/submissionService';

export const openMonitoringForActivatedTest = (assessmentRunId: number, platformAdminUrl: string, properties: any, customFilters: any = []): void => {
    const assessmentState: AssessmentState = (store.getState() as RootState).assessment;
    let url = `${platformAdminUrl}#live/data/`;

    const filters = {
        assessmentRunId,
        ...properties,
    };

    const urlFilterData = {
        filters,
        page: 1,
        hitsPerPage: 20,
        deliveringContext: true,
        assessmentRunTitle: assessmentState.assessmentRunTitle,
        columns: hasCurrentUserEvaluatorPrivilege() && !hasCurrentUserPlannerPrivilege() ? [
            {
                name: 'evaluationRounds',
                disableSort: true,
                marketplacePropertyDependency: 'ENABLE_REASSESSMENT',
            },
            {
                name: 'candidateId',
                defaultColumn: true,
                sortField: 'candidateId.raw',
            },
            {
                name: 'progress',
                sortField: 'progressTimestamp',
            },
            {
                name: 'assessmentRunTitle',
                sortField: 'assessmentRunTitle.raw',
            },
            {
                name: 'committeeName',
            },
            {
                name: 'explanationOfGradeStatus',
                marketplacePropertyDependency: 'GRADE_EXPLANATION_ADMINISTRATION_ENABLED',
            },
            {
                name: 'explanationOfGradesRequestStatus',
                marketplacePropertyDependency: 'GRADE_EXPLANATION_ADMINISTRATION_ENABLED',
            },
            {
                name: 'gradingDeadline',
                marketplacePropertyDependency: 'ENABLE_GRADING_CANDIDATE_LEVEL_AND_DEADLINE',
            },
            {
                name: 'restrictedUntilDate',
            },
            {
                name: 'externalIds',
                sortField: 'externalIds.externalId',
                marketplacePropertyDependency: 'ENABLE_CANDIDATE_EXTERNAL_ID_COLUMN',
            },
        ] : [
            {
                name: 'statusIcon',
                type: 'static',
                disableSort: true,
            },
            {
                name: 'candidateId',
                order: 'asc',
            },
            {
                name: 'status',
            },
            {
                name: 'progress',
                sortField: 'progressTimestamp',
            },
            {
                name: 'warnings',
                sortField: 'warnings.timestamp',
            },
            {
                name: 'minutesSpent',
                disableSort: true,
            },
            {
                name: 'lastSaved',
            },
            {
                name: 'extraTime',
            },
            {
                name: 'attendanceStatus',
            },
        ],
        ...customFilters,
    };
    url += encodeURIComponent(JSON.stringify(urlFilterData));
    const newTab = window.open(url);
    if (newTab) {
        newTab.name = 'deliveringMonitoringCandidates';
    }
};

export const openNewTabInPlatform = (actionName: string) => {
    const platformAdminUrl = urlService.prependDomain('/admin#');
    const { context, assessment } = store.getState() as RootState;
    const { assessmentRunId, selectedCommitteeId, partyId } = context;
    const { questionSetContentItemId, isAssessmentPath } = assessment;
    const platformDeliveryUrl = `${platformAdminUrl}deliver-test/${assessmentRunId}`;
    const usualFilters = {
        assessmentRunStatus: '$all',
        warnings: '$all',
        properties: '$all',
    };
    let urlLog: any;
    let conf;

    switch (actionName) {
    case OptionsMenuActionsName.OPEN_GRADING_PROTOCOL:
        let url = `${platformAdminUrl}grading/finalGrades/${assessmentRunId}`;
        if (selectedCommitteeId > 0) {
            url += `/${selectedCommitteeId}`;
        }
        window.open(url);
        break;
    case OptionsMenuActionsName.VIEW_CANDIDATE_IN_MONITOR:
        openMonitoringForActivatedTest(assessmentRunId, platformAdminUrl, { properties: '$all' });
        break;
    case OptionsMenuActionsName.VIEW_CANDIDATE_IN_MONITOR_SUBMITTED:
        conf = {
            ...usualFilters,
            progress: [
                'delivered',
                'delivered_blank',
            ],
        };
        openMonitoringForActivatedTest(assessmentRunId, platformAdminUrl, conf);
        break;
    case OptionsMenuActionsName.VIEW_CANDIDATE_IN_MONITOR_IN_PROGRESS:
        conf = {
            ...usualFilters,
            progress: [
                'in_progress',
            ],
        };
        openMonitoringForActivatedTest(assessmentRunId, platformAdminUrl, conf);
        break;
    case OptionsMenuActionsName.VIEW_CANDIDATE_IN_MONITOR_NOT_SUBMITTED:
        conf = {
            ...usualFilters,
            progress: [
                'not_logged_in',
                'logged_in',
                'pending_readiness_check',
                'readiness_check_in_progress',
                'ready',
                'in_progress',
                'run_paused',
            ],
        };
        const customColumns = {
            columns: [
                {
                    name: 'statusIcon',
                },
                {
                    name: 'candidateId',
                    sortField: 'candidateId.raw',
                    order: 'asc',
                },
                {
                    name: 'status',
                },
                {
                    name: 'progress',
                    sortField: 'progressTimestamp',
                },
                {
                    name: 'openForReDelivery',
                },
                {
                    name: 'startTime',
                },
                {
                    name: 'assessmentRunEndTime',
                },
                {
                    name: 'endTime',
                },
            ],
        };
        openMonitoringForActivatedTest(assessmentRunId, platformAdminUrl, conf, customColumns);
        break;

    case OptionsMenuActionsName.VIEW_CANDIDATE_IN_MONITOR_OPEN_FOR_RESUBMISSION:
        conf = {
            ...usualFilters,
            openForReDelivery: true,
        };
        const customFilters = {
            columns: [
                {
                    name: 'statusIcon',
                },
                {
                    name: 'candidateId',
                    sortField: 'candidateId.raw',
                },
                {
                    name: 'status',
                },
                {
                    name: 'progress',
                    sortField: 'progressTimestamp',
                },
                {
                    name: 'openForReDelivery',
                    order: 'desc',
                },
                {
                    name: 'startTime',
                },
                {
                    name: 'assessmentRunEndTime',
                },
                {
                    name: 'endTime',
                },
            ],
        };
        openMonitoringForActivatedTest(assessmentRunId, platformAdminUrl, conf, customFilters);
        break;
    case OptionsMenuActionsName.VIEW_CANDIDATE_IN_MONITOR_WITHDRAWN:
        conf = {
            ...usualFilters,
            progress: [
                'delivered_withdraw',
            ],
        };
        openMonitoringForActivatedTest(assessmentRunId, platformAdminUrl, conf);
        break;
    case OptionsMenuActionsName.OPEN_TEST_IN_DELIVER:
        const deliverUrl = isAssessmentPath ? getAPUrl(assessmentRunId, 'summary')
            : platformDeliveryUrl;
        window.open(deliverUrl);
        break;
    case OptionsMenuActionsName.VIEW_QUESTION_SET_AS_STUDENT:
        const previewUrl = submissionService.getQuestionSetViewUrl(assessmentRunId, partyId, Context.PREVIEW, false);
        window.open(urlService.prependDomain(previewUrl));
        break;
    case OptionsMenuActionsName.VIEW_QUESTION_SET_WITH_CORRECT_ANSWERS:
        const deliveryUrl = submissionService.getQuestionSetViewUrl(assessmentRunId, partyId, Context.DELIVERY, false);
        window.open(urlService.prependDomain(deliveryUrl));
        break;
    case OptionsMenuActionsName.MANAGE_CONTRIBUTORS:
        const contributorsUrl = isAssessmentPath ? getAPUrl(assessmentRunId, 'contributors')
            : `${platformDeliveryUrl}?manageContributors=true`;
        window.open(contributorsUrl);
        break;
    case OptionsMenuActionsName.MANAGE_COMMITTEES:
        const committeesUrl = isAssessmentPath ? getAPUrl(assessmentRunId, 'committees')
            : `${platformDeliveryUrl}?manageCommittees=true`;
        window.open(committeesUrl);
        break;
    case OptionsMenuActionsName.INVITE_GRADER:
        window.open(`${platformDeliveryUrl}?inviteGrader=true`);
        break;
    case OptionsMenuActionsName.EDIT_VIEW:
        window.open(`${platformAdminUrl}author/test/${questionSetContentItemId}`);
        break;
    case OptionsMenuActionsName.EVENT_LOG:
        urlLog = encodeURIComponent(JSON.stringify({
            page: 1,
            hitsPerPage: 10,
            filters: {
                eventType: '$all',
                contextObjectId: assessmentRunId,
            },
        }));
        window.open(`${platformAdminUrl}log/data/${urlLog}`);
        break;
    }
};

function getAPUrl(assessmentRunId: string | number, tab?: string): string {
    const pathToAP = urlService.prependDomain('/deliver/#/delivery');
    return `${pathToAP}/${assessmentRunId}/${tab || ''}`;
}
